import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, uploadFile } from '../client';

// ---------- Self-service ----------

export interface ChangePasswordInput {
  current_password: string;
  new_password: string;
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (input: ChangePasswordInput) =>
      api.post<{ ok: true }>('/api/auth/change-password', input),
  });
}

export interface TwoFactorEnrollResponse {
  secret_base32: string;
  otpauth_uri: string;
  qr_code_data_url: string;
}

export function useEnrollTwoFactor() {
  return useMutation({
    mutationFn: () => api.post<TwoFactorEnrollResponse>('/api/auth/2fa/enroll', {}),
  });
}

export function useVerifyTwoFactor() {
  return useMutation({
    mutationFn: (code: string) =>
      api.post<{ ok: true }>('/api/auth/2fa/verify', { code }),
  });
}

export interface DisableTwoFactorInput {
  current_password: string;
  code: string;
}

export interface RegenBackupCodesInput {
  current_password: string;
  code: string;
}

export function useRegenerateBackupCodes() {
  return useMutation({
    mutationFn: (input: RegenBackupCodesInput) =>
      api.post<{ codes: string[]; note: string }>(
        '/api/auth/2fa/backup-codes',
        input,
      ),
  });
}

export function useBackupCodesCount() {
  return useQuery({
    queryKey: ['backup-codes-count'],
    queryFn: ({ signal }) =>
      api.get<{ remaining: number }>('/api/auth/2fa/backup-codes-count', signal),
    staleTime: 30_000,
  });
}

// ---------- System settings ----------

export type SystemSettingsBag = {
  /** `address` is the legacy free-text blob — the seed <AddressFields> auto-parses
   *  from, cleared once the structured fields are saved. The structured fields are
   *  optional because a settings row written before they existed has no such keys. */
  company?: {
    name: string;
    address: string | null;
    address_line1?: string | null;
    address_line2?: string | null;
    city?: string | null;
    state?: string | null;
    postal_code?: string | null;
    country?: string | null;
    email: string | null;
    phone: string | null;
    website: string | null;
  };
  timezone?: { zone: string };
  password_policy?: { min_length: number; require_upper: boolean; require_lower: boolean; require_digit: boolean; require_symbol: boolean };
  ip_whitelist?: { enabled: boolean; cidrs: string[] };
  session_policy?: { idle_timeout_minutes: number };
  rate_limit?: { login_attempts_per_15min: number; api_requests_per_minute: number };
  audit_retention?: { days: number };
  document_storage?: { root_path: string; previous_roots?: string[] };
  /** generic_vendor value → expense_type value, for inferring an expense's
   *  Type from a receipt's vendor. Admin-managed; see SettingsPage. */
  generic_vendor_types?: Record<string, string>;
};

export function useSystemSettings() {
  return useQuery({
    queryKey: ['system-settings'],
    queryFn: ({ signal }) => api.get<{ settings: SystemSettingsBag }>('/api/settings', signal),
  });
}

// Read-only effective-root status for the Document storage card. Computes
// the env fallback + writability the generic settings bag can't.
export interface StorageStatus {
  effective_root: string;
  configured_root: string | null;
  previous_roots: string[];
  writable: boolean;
  allowed_roots: string[];
}

export function useStorageStatus() {
  return useQuery({
    queryKey: ['storage-status'],
    queryFn: ({ signal }) => api.get<StorageStatus>('/api/storage/status', signal),
  });
}

export function useUpdateSystemSetting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, value }: { key: keyof SystemSettingsBag; value: unknown }) =>
      api.patch<{ key: string; value: unknown }>(`/api/settings/${key}`, value),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['system-settings'] });
      // The Document storage card derives its "currently storing in" from
      // a separate status endpoint — refresh it after any save.
      void qc.invalidateQueries({ queryKey: ['storage-status'] });
    },
  });
}

// Uploads the user's profile photo. Now writes the linked person's photo
// (people_photos) — the same image the Directory shows — so profile + nav +
// directory stay in sync. See ProfileTab / GET /api/users/:id/avatar.
export function useUploadAvatar() {
  return useMutation({
    mutationFn: (file: File) =>
      uploadFile<{ ok: true }>('/api/users/me/avatar', file),
  });
}

export function useDisableTwoFactor() {
  return useMutation({
    mutationFn: (input: DisableTwoFactorInput) =>
      api.post<{ ok: true }>('/api/auth/2fa/disable', input),
  });
}

// ---------- User management (admin) ----------

export interface AdminUser {
  id: number;
  email: string;
  display_name: string;
  is_active: boolean;
  is_2fa_enabled: boolean;
  last_login_at: number | null;
  created_at: number;
  updated_at: number;
  role_names: string[];
  /** The directory person this login is linked to (set on create). */
  person_id: number | null;
}

export function useAdminUsers(includeInactive: boolean) {
  const url = includeInactive ? '/api/users?include_inactive=true' : '/api/users';
  return useQuery({
    queryKey: ['admin-users', { includeInactive }],
    queryFn: ({ signal }) => api.get<{ users: AdminUser[] }>(url, signal),
  });
}

export interface CreateUserInput {
  email: string;
  display_name: string;
  password: string;
  role_names: string[];
  /** Link the login to an existing directory person (the "make staff" path). */
  person_id?: number;
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateUserInput) =>
      api.post<{ user_id: number; person_id: number | null }>('/api/users', input),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['admin-users'] });
      // A new staff person shows up under the Staff directory + on its page.
      void qc.invalidateQueries({ queryKey: ['directory'] });
      if (vars.person_id !== undefined) {
        void qc.invalidateQueries({ queryKey: ['person', vars.person_id] });
      }
    },
  });
}

export interface UpdateAdminUserInput {
  id: number;
  display_name?: string;
  is_active?: boolean;
  role_names?: string[];
}

export function useUpdateAdminUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateAdminUserInput) =>
      api.patch<void>(`/api/users/${id}`, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });
}

export function useResetUserPassword() {
  return useMutation({
    mutationFn: ({ id, new_password }: { id: number; new_password: string }) =>
      api.post<void>(`/api/users/${id}/reset-password`, { new_password }),
  });
}

export function useForceLogoutUser() {
  return useMutation({
    mutationFn: (id: number) =>
      api.post<{ revoked: number }>(`/api/users/${id}/force-logout`, {}),
  });
}

// ---------- RBAC (admin) ----------

export interface PermissionRow {
  id: number;
  name: string;
  description: string;
}

export function useRbacPermissions() {
  return useQuery({
    queryKey: ['rbac-permissions'],
    queryFn: ({ signal }) => api.get<{ permissions: PermissionRow[] }>('/api/rbac/permissions', signal),
    staleTime: 5 * 60 * 1000,
  });
}

export interface RoleRow {
  id: number;
  name: string;
  description: string | null;
  permission_names: string[];
}

export function useRbacRoles() {
  return useQuery({
    queryKey: ['rbac-roles'],
    queryFn: ({ signal }) => api.get<{ roles: RoleRow[] }>('/api/rbac/roles', signal),
  });
}

export function useUpdateRolePermissions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, permission_names }: { id: number; permission_names: string[] }) =>
      api.patch<void>(`/api/rbac/roles/${id}`, { permission_names }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rbac-roles'] });
    },
  });
}
